// Shell de layout del panel: sidebar de navegación + topbar.
//
// No es un Web Component: conoce la lista de módulos del Panel Super Admin,
// así que es específico de esta app. El CSS del shell (sidebar/topbar/login/
// dashboard) vive en @plataforma/ui/src/estilos-layout.css.
//
// Responsive: en móvil (<720px) el sidebar se oculta y se muestra un botón
// hamburguesa en el topbar. Al pulsarlo, el sidebar se desliza desde la
// izquierda con un backdrop que lo cierra al tocarlo.

import {
  iconoDashboard,
  iconoEmpresas,
  iconoUsuarios,
  iconoDominios,
  iconoContrataciones,
  iconoPublicaciones,
  iconoProductos,
  iconoCupones,
  iconoLlave,
  iconoEnlace,
  iconoUsuario,
} from '@plataforma/ui';

import { cerrarSesion, obtenerEstadoSesion } from '../estado-sesion.js';

const MODULOS = [
  { ruta: '/', etiqueta: 'Dashboard', icono: iconoDashboard },
  { ruta: '/ingresos', etiqueta: 'Ingresos', icono: iconoCupones },
  { ruta: '/empresas', etiqueta: 'Empresas', icono: iconoEmpresas },
  { ruta: '/usuarios', etiqueta: 'Usuarios', icono: iconoUsuarios },
  { ruta: '/planes', etiqueta: 'Planes', icono: iconoProductos },
  { ruta: '/contrataciones', etiqueta: 'Contrataciones', icono: iconoContrataciones },
  { ruta: '/suscripciones', etiqueta: 'Suscripciones', icono: iconoLlave },
  { ruta: '/trials', etiqueta: 'Trials', icono: iconoContrataciones },
  { ruta: '/pagos', etiqueta: 'Facturación / pagos', icono: iconoCupones },
  { ruta: '/beneficios', etiqueta: 'Beneficios', icono: iconoCupones },
  { ruta: '/promociones', etiqueta: 'Promociones', icono: iconoCupones },
  { ruta: '/actividad', etiqueta: 'Actividad', icono: iconoDashboard },
  { ruta: '/auditoria', etiqueta: 'Auditoría', icono: iconoLlave },
  { ruta: '/dominios', etiqueta: 'Dominios', icono: iconoDominios },
  { ruta: '/publicaciones', etiqueta: 'Publicaciones', icono: iconoPublicaciones },
  { ruta: '/vimos-outbox', etiqueta: 'Vimos · Outbox', icono: iconoEnlace },
  { ruta: '/configuracion', etiqueta: 'Configuración', icono: iconoLlave },
  { ruta: '/mi-empresa', etiqueta: 'Mi empresa', icono: iconoEmpresas },
];

/**
 * @param {HTMLElement} contenedorRaiz
 * @param {string} rutaActiva
 * @returns {HTMLElement} el nodo donde la vista debe montar su contenido.
 */
export function montarShell(contenedorRaiz, rutaActiva) {
  const { sesion } = obtenerEstadoSesion();
  const correo = sesion?.user?.email ?? '';

  contenedorRaiz.innerHTML = `
    <div class="pf-shell" id="pf-shell">
      <div class="pf-shell__backdrop" id="pf-sidebar-backdrop"></div>
      <aside class="pf-shell__sidebar" id="pf-sidebar">
        <div class="pf-shell__marca">
          <img src="/logo.svg" alt="Vimos Digital" class="pf-shell__marca-logo" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';" />
          <div class="pf-shell__marca-texto pf-shell__marca-texto--fallback" style="display:none;">
            <span class="pf-shell__marca-nombre">Vimos Digital</span>
            <span>Super Admin</span>
          </div>
        </div>
        <nav class="pf-shell__nav">
          ${MODULOS.map(
            (m) => `
            <a href="#${m.ruta}" class="pf-shell__enlace${m.ruta === rutaActiva ? ' pf-shell__enlace--activo' : ''}">
              <span class="pf-shell__enlace-icono">${m.icono()}</span>
              <span class="pf-shell__enlace-texto">${m.etiqueta}</span>
            </a>`,
          ).join('')}
        </nav>
      </aside>
      <div class="pf-shell__principal">
        <header class="pf-shell__topbar">
          <div class="pf-shell__topbar-izquierda">
            <button type="button" class="pf-shell__hamburger" id="pf-hamburger" aria-label="Abrir menú" aria-expanded="false">
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
          <div class="pf-shell__topbar-derecha">
            <span class="pf-shell__usuario">
              <span class="pf-shell__usuario-icono">${iconoUsuario()}</span>
              ${correo}
            </span>
            <a href="#/cambiar-contrasena" class="pf-boton pf-boton--secundario pf-boton--chico">
              Cambiar contraseña
            </a>
            <button type="button" class="pf-boton pf-boton--secundario pf-boton--chico" id="pf-boton-salir">
              Cerrar sesión
            </button>
          </div>
        </header>
        <main class="pf-shell__contenido"></main>
      </div>
    </div>
  `;

  const sidebar = contenedorRaiz.querySelector('#pf-sidebar');
  const backdrop = contenedorRaiz.querySelector('#pf-sidebar-backdrop');
  const hamburger = contenedorRaiz.querySelector('#pf-hamburger');

  function abrirMenu() {
    sidebar.classList.add('pf-shell__sidebar--abierto');
    backdrop.classList.add('pf-shell__backdrop--visible');
    hamburger.classList.add('pf-shell__hamburger--activo');
    hamburger.setAttribute('aria-expanded', 'true');
    hamburger.setAttribute('aria-label', 'Cerrar menú');
  }

  function cerrarMenu() {
    sidebar.classList.remove('pf-shell__sidebar--abierto');
    backdrop.classList.remove('pf-shell__backdrop--visible');
    hamburger.classList.remove('pf-shell__hamburger--activo');
    hamburger.setAttribute('aria-expanded', 'false');
    hamburger.setAttribute('aria-label', 'Abrir menú');
  }

  function toggleMenu() {
    if (sidebar.classList.contains('pf-shell__sidebar--abierto')) {
      cerrarMenu();
    } else {
      abrirMenu();
    }
  }

  hamburger.addEventListener('click', toggleMenu);
  backdrop.addEventListener('click', cerrarMenu);

  // Cerrar el menú al navegar en móvil (al seleccionar un enlace)
  sidebar.querySelectorAll('.pf-shell__enlace').forEach((enlace) => {
    enlace.addEventListener('click', () => {
      if (window.innerWidth <= 720) {
        cerrarMenu();
      }
    });
  });

  // Cerrar con Escape
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && sidebar.classList.contains('pf-shell__sidebar--abierto')) {
      cerrarMenu();
    }
  });

  contenedorRaiz.querySelector('#pf-boton-salir').addEventListener('click', async () => {
    await cerrarSesion();
  });

  return contenedorRaiz.querySelector('.pf-shell__contenido');
}
